import React from 'react';
import { Heart, Video, Users, Target, Lightbulb, ExternalLink, GraduationCap, Star } from 'lucide-react';

const GoFundMeSection = () => {
  return (
    <section id="gofundme" style={{ 
      padding: '5rem 1.5rem', 
      background: 'linear-gradient(135deg, #C5A059 0%, #EE5A24 50%, #C5A059 100%)'
    }}>
      <div className="container">
        {/* Main Content Card */}
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #C5A059 0%, #EE5A24 100%)',
            padding: '2rem',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '8px 20px',
              borderRadius: '9999px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              <Heart size={18} fill="white" />
              Support Our Mission
            </div>
            <h2 style={{ 
              fontSize: '2rem', 
              fontWeight: 800, 
              marginBottom: '0.5rem',
              lineHeight: 1.2
            }}>
              Help Make GCSE Maths Exciting! 🎬
            </h2>
            <p style={{ opacity: 0.95, fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
              Join Dr. Michael Omakobia's mission to create FREE animated videos for every GCSE Maths topic
            </p>
          </div>

          {/* Content */}
          <div style={{ padding: '2rem' }}>
            {/* The Problem */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                color: '#EE5A24',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Lightbulb size={24} />
                The Problem Every Student Faces
              </h3>
              <blockquote style={{
                borderLeft: '4px solid #EE5A24',
                paddingLeft: '1rem',
                margin: '0 0 1rem 0',
                fontStyle: 'italic',
                color: '#666',
                fontSize: '1.1rem'
              }}>
                "Why do I even need to know this?"
              </blockquote>
              <p style={{ color: 'var(--text-body)', lineHeight: 1.7 }}>
                With 12 years of teaching experience and as an <strong>IGCSE Maths Examiner</strong>, I've heard this question thousands of times. Bright students switch off and lose confidence - not because they can't do maths, but because they can't see the point. <strong>Traditional teaching doesn't connect to their world.</strong>
              </p>
            </div>

            {/* The Vision */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(238, 90, 36, 0.08) 0%, rgba(255, 107, 107, 0.08) 100%)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                color: '#EE5A24',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Video size={24} />
                The Vision: 100+ Animated GCSE Maths Videos
              </h3>
              <p style={{ color: 'var(--text-body)', marginBottom: '1rem', lineHeight: 1.7 }}>
                Imagine if every GCSE maths topic came alive through <strong>professional animated videos</strong> that show students:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: '#C5A059', fontWeight: 700 }}>✅</span>
                  <span><strong>The "WHY"</strong> - Real-world applications they actually care about</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: '#C5A059', fontWeight: 700 }}>✅</span>
                  <span><strong>The "HOW"</strong> - Step-by-step visual explanations that stick</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ color: '#C5A059', fontWeight: 700 }}>✅</span>
                  <span><strong>The "WOW"</strong> - Engaging animations that make maths exciting</span>
                </div>
              </div>
            </div>

            {/* Example */}
            <div style={{
              background: '#1a1a2e',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '2rem',
              color: 'white'
            }}>
              <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#E5C575' }}>
                📐 Example: SOHCAHTOA (Trigonometry)
              </div>
              <p style={{ opacity: 0.9, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                Instead of boring abstract triangles, students will see:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.9rem' }}>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '8px' }}>🏗️ Architects calculating heights</span>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '8px' }}>✈️ Pilots navigating descent</span>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '8px' }}>🎮 Game developers creating physics</span>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '8px' }}>🥾 Hikers measuring slopes</span>
              </div>
            </div>

            {/* Impact Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                background: 'var(--accent-wash)',
                padding: '1.25rem 1rem',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent-text)' }}>700K+</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>UK Students Take GCSE Maths Yearly</div>
              </div>
              <div style={{
                background: 'var(--accent-wash)',
                padding: '1.25rem 1rem',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent-text)' }}>100+</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Topics to Animate</div>
              </div>
              <div style={{
                background: 'var(--accent-wash)',
                padding: '1.25rem 1rem',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent-text)' }}>FREE</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>For All Students</div>
              </div>
            </div>

            {/* Quote */}
            <div style={{
              background: 'linear-gradient(135deg, #E5C575 0%, #C5A059 100%)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <p style={{ 
                fontSize: '1.1rem', 
                fontWeight: 600, 
                color: '#1a1a2e',
                fontStyle: 'italic',
                marginBottom: '0.5rem'
              }}>
                "It's not IQ that determines how well students do in exams - it's practice, practice, practice! And practice becomes possible when students actually WANT to learn."
              </p>
              <p style={{ fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                — Dr. Michael Omakobia
              </p>
            </div>

            {/* CTA */}
            <div style={{ textAlign: 'center' }}>
              <a
                href="https://gofund.me/a95e4500"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  background: 'linear-gradient(135deg, #C5A059 0%, #EE5A24 100%)',
                  color: 'white',
                  padding: '1rem 2.5rem',
                  borderRadius: '9999px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 8px 24px rgba(238, 90, 36, 0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(238, 90, 36, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(238, 90, 36, 0.4)';
                }}
              >
                <Heart size={22} fill="white" />
                Support on GoFundMe
                <ExternalLink size={18} />
              </a>
              <p style={{ 
                marginTop: '1rem', 
                color: 'var(--text-secondary)',
                fontSize: '0.9rem'
              }}>
                Every donation helps create engaging maths content for students who need it most
              </p>
            </div>
          </div>

          {/* Footer with Rewards Preview */}
          <div style={{
            background: '#f8f9fa',
            padding: '1.5rem 2rem',
            borderTop: '1px solid #eee'
          }}>
            <div style={{ 
              fontSize: '0.9rem', 
              fontWeight: 600, 
              color: 'var(--text-secondary)',
              marginBottom: '0.75rem',
              textAlign: 'center'
            }}>
              🎁 Supporter Rewards Include:
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.8rem'
            }}>
              <span style={{ background: '#e9ecef', padding: '4px 12px', borderRadius: '9999px' }}>£5+ Name in Credits</span>
              <span style={{ background: '#e9ecef', padding: '4px 12px', borderRadius: '9999px' }}>£50+ Choose a Topic</span>
              <span style={{ background: '#e9ecef', padding: '4px 12px', borderRadius: '9999px' }}>£100+ Sponsor a Video</span>
              <span style={{ background: '#e9ecef', padding: '4px 12px', borderRadius: '9999px' }}>£500+ Consultation with Dr. O</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoFundMeSection;
